import _ from 'lodash';

import { Common } from './common';
import type {
  AssistantReqAuditParams,
  Modality,
  ProviderFilters,
  SourceComponent,
} from './models/assistant';
import type { FeedbackType } from './models/feedback';
import type { ConfigurationWithSession } from './models/organization/assistant/configuration';
import type { ChatMessageType } from './models/report/feedback';
import type { UserWithProfileAndPreference } from './models/user';
import type { Patient } from './models/patient';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async addMessageToConversationToStream(args: {
    message: ChatMessageType;
    configurationId: string;
    sourceComponent: SourceComponent;
    sessionId?: string;
    isEmbedded?: boolean;
  }) {
    const url = this.common.constructPath(
      `/patient-previsit/assistant/conversation`,
    );
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
      cache: 'no-cache',
      body: JSON.stringify(_.omit(args, 'includeCredentials')),
    });
    if (!response?.body) {
      throw new Error('No response body');
    }
    return response.body.getReader();
  }

  async createMessageFeedback(args: {
    messageId: string;
    feedback: FeedbackType;
    comment: string;
    configurationId: string;
  }) {
    const url = this.common.constructPath(
      `/patient-previsit/assistant/conversation/messages/${args.messageId}/feedbacks`,
    );
    return this.common.postJSON(url, _.omit(args, 'messageId'), 'POST');
  }

  async getConfigurationById(
    id: string,
    modality?: Modality,
    isEmbedded?: boolean,
  ): Promise<ConfigurationWithSession> {
    const url = this.common.constructPath(
      `/patient-previsit/assistant/configurations/${id}`,
      { isEmbedded, modality },
    );
    return this.common.getJSON(url);
  }

  createSpeechToTextWebSocket(): WebSocket {
    const url = this.common.constructWsPath(
      '/patient-previsit/assistant/speech-to-text',
    );
    return new WebSocket(url);
  }

  async getProviders(
    configurationId: string,
    providerUserIds: string[],
  ): Promise<UserWithProfileAndPreference[]> {
    const url = this.common.constructPath(
      `/patient-previsit/assistant/providers`,
      { configurationId, providerUserIds },
    );
    return this.common.getJSON(url);
  }

  async getSearchProviderFilters(
    configurationId: string,
  ): Promise<ProviderFilters> {
    const url = this.common.constructPath(
      `/patient-previsit/assistant/providers/search/filters`,
      { configurationId },
    );
    return this.common.getJSON(url);
  }

  async me(): Promise<Patient> {
    const url = this.common.constructPath(
      '/patient-previsit/assistant/patients/me',
    );
    const { patient } = await this.common.getJSON(url);
    return patient;
  }

  async createLog(args: AssistantReqAuditParams): Promise<boolean> {
    const url = this.common.constructPath('/patient-previsit/assistant/logs');
    const { success } = await this.common.postJSON(url, args, 'POST');
    return success;
  }
}
